import React, { useEffect, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { api } from "@/lib/api";
import { formatPrice } from "@/components/ListingCard";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const NAVY = "#0A192F";
const GOLD = "#C19D60";
const PALETTE = [NAVY, GOLD, "#334155", "#94A3B8", "#D4B886"];

const STAGE_LABEL = { prospecting: "Prospecting", offer: "Offer", negotiation: "Negotiation", contract: "Contract", closing: "Closing", closed: "Closed" };
const STAGES = ["prospecting", "offer", "negotiation", "contract", "closing", "closed"];

const Stat = ({ label, value, hint, testId }) => (
  <div className="bg-white border border-slate-200 p-6" data-testid={testId}>
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-3xl text-navy mt-2">{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

const Dashboard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/crm/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <CRMLayout><div className="text-slate-500">Loading…</div></CRMLayout>;

  const pipelineData = STAGES.map((s) => {
    const match = d.pipeline.find((p) => p.stage === s);
    return { stage: STAGE_LABEL[s], count: match ? match.count : 0, value: match ? match.value : 0 };
  });

  return (
    <CRMLayout>
      <div className="mb-8" data-testid="crm-dashboard-page">
        <div className="overline text-gold">Dashboard</div>
        <h1 className="font-display text-3xl text-navy mt-2">Welcome back.</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat testId="stat-leads" label="Leads" value={d.widgets.leads_total} hint={`${d.widgets.leads_new} new`} />
        <Stat testId="stat-active-deals" label="Active Deals" value={d.widgets.deals_active} />
        <Stat testId="stat-closed-deals" label="Closed" value={d.widgets.deals_closed} />
        <Stat testId="stat-revenue" label="Revenue" value={formatPrice(d.widgets.revenue)} hint="Closed deals" />
        <Stat testId="stat-appointments" label="Appointments" value={d.widgets.appointments} />
        <Stat testId="stat-active-listings" label="Active Listings" value={d.widgets.listings_active} />
        <Stat testId="stat-tasks" label="Tasks" value="—" hint="Add tasks in V2" />
        <Stat testId="stat-agents-active" label="Team" value={d.agents.length || 1} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline text-gold">Deal Pipeline</div>
              <h3 className="font-display text-xl text-navy mt-1">By stage</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={pipelineData} margin={{ left: 0, right: 0, top: 10, bottom: 10 }}>
                <XAxis dataKey="stage" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #0A192F", fontFamily: "Manrope" }} />
                <Bar dataKey="count" fill={GOLD} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6">
          <div className="overline text-gold">Lead Sources</div>
          <h3 className="font-display text-xl text-navy mt-1 mb-2">Attribution</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d.sources} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                  {d.sources.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={24} iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <div className="overline text-gold">Agent Performance</div>
        <h3 className="font-display text-xl text-navy mt-1 mb-4">Closed deals & revenue</h3>
        {d.agents.length === 0 ? (
          <div className="text-sm text-slate-500">No closed deals attributed yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                <th className="py-2">Agent</th><th>Deals Closed</th><th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {d.agents.map((a) => (
                <tr key={a.agent} className="border-b border-slate-100">
                  <td className="py-3 font-semibold text-navy">{a.agent}</td>
                  <td>{a.deals}</td>
                  <td>{formatPrice(a.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </CRMLayout>
  );
};

export default Dashboard;
